import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { proposalService } from '@/services/proposalService';

interface ChatReportCardProps {
  proposalId: string;
  tenantId: string | null;
  onOpen?: () => void;
}

/**
 * Inline preview of a generated proposal report inside the Emjae chat: a scaled
 * live render of the actual branded HTML, plus a Download-PDF button. The HTML is
 * fetched by id (GET /proposals/{id}/report) so it never bloats the SSE stream.
 */
export function ChatReportCard({ proposalId, tenantId, onOpen }: ChatReportCardProps) {
  const navigate = useNavigate();
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    proposalService
      .getProposalReport(proposalId, tenantId ?? '')
      .then((fetched) => {
        if (!cancelled) setHtml(fetched);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId, tenantId]);

  const open = () => {
    onOpen?.();
    navigate(`/proposals/${proposalId}`);
  };

  const download = async () => {
    if (!html) return;
    setDownloading(true);
    try {
      const blob = await proposalService.renderPdfFromHtml(html, 'proposal');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `proposal-${proposalId.slice(0, 8)}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-warm-border bg-paper">
      <button
        type="button"
        onClick={open}
        title="Open full report"
        className="relative block h-[210px] w-full overflow-hidden bg-warm-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
      >
        {loading && (
          <span className="flex h-full items-center justify-center text-[11px] text-ink-3">
            Rendering preview…
          </span>
        )}
        {failed && (
          <span className="flex h-full items-center justify-center gap-1.5 text-[11px] text-ink-3">
            <FileText className="size-3.5" /> Report ready — open to view
          </span>
        )}
        {html && (
          <iframe
            title="Proposal report preview"
            srcDoc={html}
            sandbox=""
            aria-hidden="true"
            tabIndex={-1}
            style={{
              width: 900,
              height: 640,
              border: 0,
              transform: 'scale(0.32)',
              transformOrigin: 'top left',
              pointerEvents: 'none',
            }}
          />
        )}
        {html && (
          <span className="pointer-events-none absolute bottom-1 right-1.5 rounded bg-ink/70 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white">
            preview
          </span>
        )}
      </button>
      <div className="flex items-center gap-2 border-t border-warm-border p-2">
        <button
          type="button"
          onClick={download}
          disabled={!html || downloading}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="size-3.5" />
          {downloading ? 'Preparing…' : 'Download PDF'}
        </button>
        <button
          type="button"
          onClick={open}
          className="inline-flex items-center gap-1.5 rounded-md border border-warm-border bg-paper-2 px-2.5 py-1.5 text-[12px] text-ink transition-colors hover:border-warm-border-strong"
        >
          <ExternalLink className="size-3.5" />
          Open full report
        </button>
      </div>
    </div>
  );
}
